import { Navigate, useLocation } from "react-router-dom";
import LoadingScreen from "../components/loading-screen";
import { useAuthContext } from "./useAuthContext";
import { useAllowedRouteRoots } from "../hooks/useAllowedNavLinks";

type RoleGuardProps = {
  children: React.ReactNode;
};

/**
 * RoleGuard Component
 *
 * Route guard that restricts access to dashboard module routes based on user role.
 * Checks the first segment of the URL path against allowed route roots for the user's role.
 *
 * Features:
 * - Automatic route validation based on role
 * - Redirects to login if not authenticated
 * - Redirects to /403 if user doesn't have access to the route
 * - Shows loading screen during authentication initialization
 *
 * @example
 * ```tsx
 * <Route
 *   path="/dashboard/*"
 *   element={
 *     <RoleGuard>
 *       <DashboardLayout />
 *     </RoleGuard>
 *   }
 * />
 * ```
 *
 * @deprecated For new code, prefer using ProtectedRoute with specific role requirements
 * @see ProtectedRoute for more granular role-based protection
 * @see useAllowedRouteRoots for the role-to-route mapping logic
 */
export default function RoleGuard({ children }: RoleGuardProps) {
  const { isAuthenticated, isInitialized, user } = useAuthContext();
  const location = useLocation();
  const allowedRouteRoots = useAllowedRouteRoots(user);

  // Show loading screen during authentication initialization
  if (!isInitialized) {
    return <LoadingScreen />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  // Extract the root segment from the current path
  const pathname = location.pathname || "/";
  const segments = pathname.split("/").filter(Boolean);
  const rootSegment = segments[0] || "";

  // Check if user is allowed to access this route
  // - If rootSegment is empty (root path), allow access
  // - If allowedRouteRoots is null, user can access everything (admin or unknown role)
  // - Otherwise, check if the root segment is in the allowed list
  const isAllowed =
    rootSegment === "" ||
    allowedRouteRoots === null ||
    allowedRouteRoots.includes(rootSegment);

  if (!isAllowed) {
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
}